import React, { useEffect, useState, useMemo } from 'react';
import AddressLookupNative from '../Shared/AddressLookupNative';
import useTravelFeeCalculation from '../../hooks/useTravelFeeCalculation';
import useAddressValidation from '../../hooks/useAddressValidation';
import { formatPriceWithCurrency } from '../../utility/priceUtils';
import { isDarkMode } from '../../utility/themeVars';

const AddressInputStep = ({
  selectedAddress,
  onAddressChange,
  primaryColor = 'blue-600',
  themeMode = 'light',
  businessName,
  bookingPage,
  slug,
  travelFee,
  onTravelFeeChange,
  currency = 'CAD',
  onValidationChange,
  searchBias = null,
}) => {
  const [outsideAvailabilityRequested, setOutsideAvailabilityRequested] = useState(false);
  const { travelFee: calculatedFee, isLoading: isCalculatingFee, error: feeError, calculateFee } = useTravelFeeCalculation(slug);
  const { validationResult, isValidating, validationError, validateAddress, resetValidation } = useAddressValidation(slug);
  const isDark = isDarkMode(themeMode);
  
  const handleAddressSelect = (address) => {
    onAddressChange(address);
    // Reset outside availability request when address changes
    setOutsideAvailabilityRequested(false);
    // Validation will be handled by the useEffect below
  };

  const handleOutsideAvailabilityToggle = (checked) => {
    setOutsideAvailabilityRequested(checked);
  };

  // Validate address when address changes
  useEffect(() => {
    if (selectedAddress?.id) {
      validateAddress(selectedAddress.id);
    } else {
      resetValidation();
    }
  }, [selectedAddress, validateAddress, resetValidation]);

  // Memoized calculation for when to calculate travel fee
  const shouldCalculateFee = useMemo(() => {
    if (!selectedAddress || !bookingPage?.travelFeesEnabled || isValidating) {
      return false;
    }
    
    const isValidAddress = validationResult?.isWithinServiceArea === true;
    const isOutsideWithRequest = bookingPage?.allowOutsideAvailability && 
                                 validationResult?.isWithinServiceArea === false && 
                                 outsideAvailabilityRequested;
    
    return isValidAddress || isOutsideWithRequest;
  }, [selectedAddress, bookingPage?.travelFeesEnabled, bookingPage?.allowOutsideAvailability, isValidating, validationResult?.isWithinServiceArea, outsideAvailabilityRequested]);

  // Calculate travel fee when address changes and is validated
  useEffect(() => {
    if (shouldCalculateFee) {
      calculateFee(selectedAddress.name);
    }
    // Note: Travel fee clearing is handled by the second useEffect
    // to prevent race conditions with async calculation
  }, [shouldCalculateFee, calculateFee, selectedAddress]);

  // Memoized calculation for when to clear travel fee
  const shouldClearTravelFee = useMemo(() => {
    if (!selectedAddress || !bookingPage?.travelFeesEnabled || isValidating) {
      return true;
    }
    
    const isInvalidWithoutRequest = validationResult?.isWithinServiceArea === false && !outsideAvailabilityRequested;
    return isInvalidWithoutRequest;
  }, [selectedAddress, bookingPage?.travelFeesEnabled, isValidating, validationResult?.isWithinServiceArea, outsideAvailabilityRequested]);

  // Memoized calculation for when to apply calculated fee
  const shouldApplyCalculatedFee = useMemo(() => {
    if (calculatedFee === null) {
      return false;
    }
    
    const isValidAddress = validationResult?.isWithinServiceArea === true;
    const isOutsideWithRequest = bookingPage?.allowOutsideAvailability && 
                                 validationResult?.isWithinServiceArea === false && 
                                 outsideAvailabilityRequested;
    
    return isValidAddress || isOutsideWithRequest;
  }, [calculatedFee, validationResult?.isWithinServiceArea, bookingPage?.allowOutsideAvailability, outsideAvailabilityRequested]);

  // Update parent state when calculated fee changes
  useEffect(() => {
    if (shouldClearTravelFee) {
      onTravelFeeChange(null);
    } else if (shouldApplyCalculatedFee) {
      onTravelFeeChange(calculatedFee);
    }
  }, [shouldClearTravelFee, shouldApplyCalculatedFee, onTravelFeeChange, calculatedFee]);

  // Update parent with validation results
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange({
        validationResult,
        isValidating,
        validationError,
        outsideAvailabilityRequested
      });
    }
  }, [validationResult, isValidating, validationError, onValidationChange, outsideAvailabilityRequested]);

  // Memoized calculation for showing travel fees
  const showTravelFee = useMemo(() => {
    if (!bookingPage?.travelFeesEnabled || !selectedAddress) {
      return false;
    }
    
    const isValidAddress = validationResult?.isWithinServiceArea === true;
    const isOutsideWithRequest = bookingPage?.allowOutsideAvailability && 
                                 validationResult?.isWithinServiceArea === false && 
                                 outsideAvailabilityRequested;
    
    return isValidAddress || isOutsideWithRequest;
  }, [bookingPage?.travelFeesEnabled, bookingPage?.allowOutsideAvailability, selectedAddress, validationResult?.isWithinServiceArea, outsideAvailabilityRequested]);


  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-themed-primary mb-2">
          Where should we come to you?
        </h3>
        <p className="text-sm text-themed-secondary">
          Enter your address where you'd like the {businessName ? `${businessName} ` : ''}appointment to take place
        </p>
      </div>

      <div className="bg-themed-card rounded-lg border border-themed p-6">
        <AddressLookupNative
          label="Your Address"
          placeholder="Enter your street address..."
          onSelect={handleAddressSelect}
          defaultValue={selectedAddress}
          required={true}
          isPublic={true}
          bookingPageSlug={slug}
          searchBias={searchBias}
          themeMode={themeMode}
          primaryColor={primaryColor}
        />
        
        {selectedAddress && (
          <div className="mt-4">
            {isValidating ? (
              <div className="p-4 bg-themed-card rounded-lg border border-themed">
                <div className="flex items-start">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 mt-0.5 mr-3 flex-shrink-0" style={{ borderBottomColor: 'var(--theme-primary)' }}></div>
                  <div>
                    <p className="text-sm font-medium text-themed-primary">Validating service area...</p>
                    <p className="text-sm text-themed-secondary mt-1">{selectedAddress.name}</p>
                  </div>
                </div>
              </div>
            ) : validationResult?.isWithinServiceArea ? (
              <div>
                <div className="p-4 bg-themed-card rounded-lg border border-themed shadow-sm">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="w-5 h-5 text-signal-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-themed-primary">Address confirmed</p>
                        <p className="text-sm text-themed-primary break-words opacity-80">{selectedAddress.name}</p>
                      </div>
                    </div>
                    {showTravelFee && (
                      <div className="flex items-center sm:flex-shrink-0">
                        {isCalculatingFee ? (
                          <div className="flex items-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 mr-2" style={{ borderBottomColor: 'var(--theme-primary)' }}></div>
                            <span className="text-sm text-themed-secondary">Calculating travel fee...</span>
                          </div>
                        ) : feeError ? (
                          <div className="text-sm text-signal-red-600">
                            <p className="font-medium">Travel fee calculation failed</p>
                            <p className="text-sm">{feeError}</p>
                          </div>
                        ) : calculatedFee !== null ? (
                          <div className="text-right sm:text-left">
                            <p className="text-sm font-medium text-themed-primary">
                              Travel Fee: <span className="font-semibold">{formatPriceWithCurrency(calculatedFee, currency)}</span>
                            </p>
                            <p className="text-sm text-themed-secondary">Added to service total</p>
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>

              </div>
            ) : validationResult?.isWithinServiceArea === false ? (
              <div className="space-y-3">
                <div className="p-4 bg-themed-card rounded-lg border border-themed shadow-sm">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="w-5 h-5 text-signal-red-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      {bookingPage?.allowOutsideAvailability ? (
                        <>
                          <p className={`text-sm font-medium ${isDark ? 'text-signal-red-100' : 'text-signal-red-600'}`}>This address is a bit outside our usual area</p>
                          <p className={`text-sm ${isDark ? 'text-signal-red-500' : 'text-signal-red-600'} mt-2`}>We may still be able to serve you here.</p>
                        </>
                      ) : (
                        <>
                          <p className={`text-sm font-medium ${isDark ? 'text-signal-red-100' : 'text-signal-red-600'}`}>This address is outside our service area</p>
                          <p className={`text-sm ${isDark ? 'text-signal-red-500' : 'text-signal-red-600'} mt-2`}>Unfortunately we can't take appointments at this location. Try a different address, or contact us directly to ask about service in your area.</p>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Outside Availability Request Option */}
                {bookingPage?.allowOutsideAvailability && (
                  <div className="p-4 bg-themed-card rounded-lg border border-themed">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="w-5 h-5 mt-0.5 text-themed-secondary" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3 flex-1">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-themed-primary">Want us to come here anyway?</p>
                            <p className="text-sm text-themed-secondary mt-1">
                              We may still be able to serve you here. Request this location and we'll confirm availability.
                            </p>
                          </div>
                        </div>
                        <div className="mt-3">
                          <label className="flex items-start">
                            <input
                              type="checkbox"
                              checked={outsideAvailabilityRequested}
                              onChange={(e) => handleOutsideAvailabilityToggle(e.target.checked)}
                              className="flex-shrink-0 h-4 w-4 border-ink-200 rounded"
                              style={{ accentColor: 'var(--theme-primary)' }}
                            />
                            <span className="ml-2 text-sm text-themed-secondary">
                              Yes, request this location
                            </span>
                          </label>
                        </div>
                        {outsideAvailabilityRequested && showTravelFee && (
                          <div className="mt-3 pt-3 border-t border-themed">
                            {isCalculatingFee ? (
                              <div className="flex items-center">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 mr-2" style={{ borderBottomColor: 'var(--theme-primary)' }}></div>
                                <span className="text-sm text-themed-secondary">Calculating travel fee...</span>
                              </div>
                            ) : feeError ? (
                              <div className="text-sm text-signal-red-600">
                                <p className="font-medium">Travel fee calculation failed</p>
                                <p>{feeError}</p>
                              </div>
                            ) : calculatedFee !== null ? (
                              <div>
                                <p className="text-sm font-medium text-themed-primary">
                                  Estimated Travel Fee: <span className="font-semibold">{formatPriceWithCurrency(calculatedFee, currency)}</span>
                                </p>
                                <p className="text-sm text-themed-secondary mt-1">
                                  This fee is subject to approval and will be added to your service total
                                </p>
                              </div>
                            ) : null}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : validationError ? (
              <div className="p-4 bg-themed-card rounded-lg border border-themed shadow-sm">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="w-5 h-5 text-signal-amber-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className={`text-sm font-medium ${isDark ? 'text-signal-amber-100' : 'text-signal-amber-600'}`}>Unable to validate service area</p>
                    <p className="text-sm text-themed-secondary break-words mt-1">{selectedAddress.name}</p>
                    <p className="text-sm text-themed-secondary mt-2">{validationError}</p>
                    <button
                      onClick={() => validateAddress(selectedAddress.id)}
                      className={`text-sm underline font-medium mt-2 ${isDark ? 'text-signal-amber-100 hover:text-signal-amber-100' : 'text-signal-amber-600 hover:text-signal-amber-600'}`}
                    >
                      Try again
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-themed-card rounded-lg border border-themed">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-themed-secondary mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-themed-primary">Selected Address:</p>
                    <p className="text-sm text-themed-secondary mt-1">{selectedAddress.name}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

      </div>

    </div>
  );
};

export default AddressInputStep;